import { useCallback, useMemo } from 'react';
import { useDimensionLabels } from './useDimensionLabels';
import useAIAlertMetricsMeta from '@src/components/ai-alerts/hooks/useAIAlertMetricsMeta';
import { isEiModule } from '@src/utils/constants';
import { AI_ALERT_STANDARD_METRICS_MAP } from '@src/components/ai-alerts/constants';

// In the video (ei) context the shared metrics meta unions in ad metrics (for
// the sensitivity/summary/diagnostics screens). Alert Rules are evaluated
// per-context, so a video rule can only ever match video alerts — an ad metric
// here would never suppress anything. Drop them so the picker only offers
// metrics that can actually match.
const AD_METRIC_KEYS = new Set(AI_ALERT_STANDARD_METRICS_MAP.AI);

export interface LabelOption {
  value: string;
  label: string;
}

// Dimension source is the current-capability subset exposed via
// `/ai-alerts/dimension-mapping` per product context. We reuse the shipped
// mute-rules hook so video/ad/eco share one dimension resolver (it already
// closes the eco/custom-dimension cases).
export const useDimensionOptions = () => {
  const { dimensionOptions, resolveDimensionLabel, productMapping } = useDimensionLabels();
  return {
    dimensionOptions: dimensionOptions as LabelOption[],
    resolveDimensionLabel,
    productMapping,
  };
};

// Metric source reuses the AI-alert metrics meta (same list the sensitivity /
// alert config screens use), so the picker always matches what the customer can
// actually alert on for the current product.
export const useMetricOptions = (enabled: boolean) => {
  const { aiAlertMetricsMeta, isFetching } = useAIAlertMetricsMeta(enabled);

  const metricOptions = useMemo<LabelOption[]>(() => {
    const seen = new Set<string>();
    return (
      (aiAlertMetricsMeta || [])
        // Prefer the human-readable metric name (`displayName`); fall back to the
        // short name and finally the raw metric key so nothing renders blank.
        .map((m: any) => ({ value: m?.name, label: m?.displayName || m?.short || m?.name }))
        .filter((opt: LabelOption) => {
          if (!opt.value || seen.has(opt.value)) return false;
          // Exclude ad metrics in the video context (see AD_METRIC_KEYS note).
          if (isEiModule && AD_METRIC_KEYS.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        })
        .sort((a: LabelOption, b: LabelOption) => a.label.localeCompare(b.label))
    );
  }, [aiAlertMetricsMeta]);

  const labelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    metricOptions.forEach((o) => {
      map[o.value] = o.label;
    });
    return map;
  }, [metricOptions]);

  const resolveMetricLabel = useCallback(
    (metricKey: string) => labelByValue[metricKey] || metricKey,
    [labelByValue],
  );

  return { metricOptions, resolveMetricLabel, isLoading: isFetching };
};
